import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useId, useState } from 'react';

export interface ShellShortcut {
  keys: string;
  label: string;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** دکمه و دیالوگ میانبرها. کلید `?` بیرون از فیلدها بازش می‌کند. */
export function ShortcutHelp({ shortcuts = [] }: { shortcuts?: readonly ShellShortcut[] }) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const rows = [{ keys: '?', label: t('common:shell.shortcut_help') }, ...shortcuts];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.ctrlKey || event.altKey || event.metaKey || event.repeat || isTypingTarget(event.target)) {
        return;
      }

      event.preventDefault();
      setOpen(true);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <Tooltip title={t('common:shell.shortcut_help')}>
        <IconButton color="inherit" aria-label={t('common:shell.shortcut_help')} aria-keyshortcuts="?" data-testid="shortcut-help" onClick={() => setOpen(true)}>
          <HelpOutlineIcon fontSize="small" />
          <Typography component="kbd" variant="caption" dir="ltr" sx={{ ms: 0.5 }}>
            ?
          </Typography>
        </IconButton>
      </Tooltip>
      {open ? (
        <Dialog open onClose={() => setOpen(false)} aria-labelledby={titleId} data-testid="shortcut-help-dialog">
          <DialogTitle id={titleId}>{t('common:shell.shortcuts_title')}</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {t('common:shell.shortcuts_hint')}
            </Typography>
            <Stack spacing={1}>
              {rows.map((row) => (
                <Stack key={`${row.keys}-${row.label}`} direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Typography>{row.label}</Typography>
                  <Typography component="kbd" variant="caption" dir="ltr" color="text.secondary" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 0.75, py: 0.25 }}>
                    {row.keys}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t('common:actions.close')}</Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
}
